// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface RequestRmaInformationStyles {
    wrapper?: GridItemProps;
    fieldWrapper?: InjectableCss;
    titleText?: TypographyProps;
    orderNumberText?: TypographyProps;
    orderDateText?: TypographyProps;
    customerPOText?: TypographyProps;
    billingInformationTitleText?: TypographyProps;
    addressDisplay?: AddressInfoDisplayStyles;
    // XNG-Changes
    billingAddressText?: TypographyProps;
    container?: GridContainerProps;
    billingGridItem?: GridItemProps;
}

export const requestRmaInformationStyles: RequestRmaInformationStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-bottom: 20px;
        `,
    },
    fieldWrapper: {
        css: css`
            margin-right: 25px;
        `,
    },
    titleText: {
        as: "h2",
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationTitleText: {
        as: "h2",
        variant: "h5",
        css: css`
            margin-bottom: 10px;
        `,
    },
};

/**
 * @deprecated Use requestRmaInformationStyles instead.
 */
export const statusStyles = requestRmaInformationStyles;
const styles = requestRmaInformationStyles;

const RequestRmaInformation: FC = () => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.wrapper}>
                <StyledWrapper {...styles.fieldWrapper}>
                    <Typography {...styles.titleText} id="requestRmaOrderNumber">
                        {translate("Order #")}
                    </Typography>
                    <Typography {...styles.orderNumberText} aria-labelledby="requestRmaOrderNumber">
                        {order.webOrderNumber}
                    </Typography>
                </StyledWrapper>
                {order.erpOrderNumber && (
                    <StyledWrapper {...styles.fieldWrapper}>
                        <Typography {...styles.titleText} id="requestRmaErpOrderNumber">
                            {translate("ERP Order #")}
                        </Typography>
                        <Typography {...styles.orderNumberText} aria-labelledby="requestRmaOrderNumber">
                            {order.erpOrderNumber}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.fieldWrapper}>
                    <Typography {...styles.titleText} id="requestRmaOrderDate">
                        {translate("Order Date")}
                    </Typography>
                    <Typography {...styles.orderDateText} aria-labelledby="requestRmaOrderDate">
                        <LocalizedDateTime
                            dateTime={order.orderDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.fieldWrapper}>
                    <Typography {...styles.titleText} id="requestRmaPONumber">
                        <span aria-hidden>{translate("PO #")}</span>
                        <VisuallyHidden>{translate("Purchase Order Number")}</VisuallyHidden>
                    </Typography>
                    <Typography {...styles.customerPOText}>{order.customerPO}</Typography>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.billingGridItem}>
                <Typography {...styles.billingInformationTitleText}>{translate("Billing Information")}</Typography>
                {/* <Typography {...styles.billingAddressText}>{translate("Billing Address")}</Typography> */}
                <AddressInfoDisplay
                    companyName={order.btCompanyName}
                    address1={order.btAddress1}
                    address2={order.btAddress2}
                    city={order.billToCity}
                    state={order.billToState}
                    postalCode={order.billToPostalCode}
                    country={order.btCountry}
                    extendedStyles={styles.addressDisplay}
                />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: RequestRmaInformation,
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
